import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import "../assets/styles/Breadcrumbs.scss";

export interface BreadcrumbItem {
  name: string;
  /** Omit on the current/last item — it isn't a link and gets no schema URL. */
  path?: string;
}

interface BreadcrumbsProps {
  /** Trail after Home, which is added automatically. */
  items: BreadcrumbItem[];
}

const SITE_URL = "https://goalguru.live";

// Renders the visible trail (replacing the old single "Back to X" link) and
// the matching BreadcrumbList JSON-LD in the same place, so the structured
// data always reflects what's actually on the page rather than drifting
// from it over time.
const Breadcrumbs: React.FC<BreadcrumbsProps> = ({ items }) => {
  const allItems: BreadcrumbItem[] = [{ name: "Home", path: "/" }, ...items];

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: allItems.map((item, index) => {
      const listItem: Record<string, unknown> = {
        "@type": "ListItem",
        position: index + 1,
        name: item.name,
      };
      // Last item is the current page — Google's own examples omit its URL.
      if (item.path && index < allItems.length - 1) {
        listItem.item = `${SITE_URL}${item.path}`;
      }
      return listItem;
    }),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        {allItems.map((item, index) => {
          const isLast = index === allItems.length - 1;
          return (
            <React.Fragment key={item.name}>
              {index > 0 && <span className="breadcrumb-sep">/</span>}
              {isLast || !item.path ? (
                <span className="breadcrumb-current" aria-current="page">
                  {item.name}
                </span>
              ) : (
                <Link to={item.path}>{item.name}</Link>
              )}
            </React.Fragment>
          );
        })}
      </nav>
    </>
  );
};

export default Breadcrumbs;
